import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Gavel } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Keputusan() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", pic: "", due_date: "", priority: "NORMAL", source_meeting: "" });

  const canCreate = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS"].includes(user?.role);

  const load = () => api.get("/decisions").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.content) { toast.error("Judul dan isi keputusan wajib diisi."); return; }
    try {
      await api.post("/decisions", form);
      toast.success("Keputusan berhasil dicatat.");
      setOpen(false);
      setForm({ title: "", content: "", pic: "", due_date: "", priority: "NORMAL", source_meeting: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const update = async (d, status, progress) => {
    try {
      await api.patch(`/decisions/${d.id}`, { status, progress });
      toast.success("Status diperbarui.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Rapat &amp; Keputusan</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Keputusan &amp; Tindak Lanjut</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Setiap keputusan menjadi actionable task dengan PIC, deadline dan progress.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-decision"><Plus className="h-4 w-4 mr-2" /> Keputusan Baru</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Keputusan Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Judul</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="input-decision-title" /></div>
                <div><Label>Isi keputusan</Label><Textarea rows={3} value={form.content} onChange={(e)=>setForm({...form, content:e.target.value})} data-testid="input-decision-content" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Asal rapat</Label><Input value={form.source_meeting} onChange={(e)=>setForm({...form, source_meeting:e.target.value})} data-testid="input-decision-meeting" /></div>
                  <div><Label>PIC</Label><Input value={form.pic} onChange={(e)=>setForm({...form, pic:e.target.value})} data-testid="input-decision-pic" /></div>
                  <div><Label>Jatuh tempo</Label><Input type="date" value={form.due_date} onChange={(e)=>setForm({...form, due_date:e.target.value})} data-testid="input-decision-due" /></div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v)=>setForm({...form, priority:v})}>
                      <SelectTrigger data-testid="select-decision-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOW">Rendah</SelectItem>
                        <SelectItem value="NORMAL">Normal</SelectItem>
                        <SelectItem value="HIGH">Tinggi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="btn-save-decision">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <Gavel className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada keputusan tercatat.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => {
            const isLate = d.due_date && d.due_date < new Date().toISOString().slice(0,10) && d.status !== "SELESAI" && d.status !== "DIBATALKAN";
            return (
              <Card key={d.id} data-testid={`row-decision-${d.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {isLate && <StatusBadge status="warning" label="TERLAMBAT" />}
                        <StatusBadge status={d.status} />
                        {d.priority === "HIGH" && <StatusBadge status="destructive" label="PRIORITAS TINGGI" />}
                      </div>
                      <div className="font-heading font-bold text-base">{d.title}</div>
                      <div className="text-sm text-muted-foreground mt-1">{d.content}</div>
                      <div className="text-xs text-muted-foreground mt-2">
                        Asal: <strong>{d.source_meeting || "—"}</strong> • PIC: <strong>{d.pic || "—"}</strong> • Jatuh tempo: <strong>{d.due_date || "—"}</strong>
                      </div>
                    </div>
                    <div className="w-full sm:w-56 shrink-0">
                      <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Progress {d.progress || 0}%</div>
                      <Progress value={d.progress || 0} className="h-1.5" />
                      {canCreate && (
                        <div className="flex gap-1 mt-3">
                          <Button size="sm" variant="outline" className="flex-1 h-7 text-[0.7rem]" onClick={() => update(d, "PROSES", Math.max(d.progress||0, 25))} data-testid={`btn-proses-${d.id}`}>Proses</Button>
                          <Button size="sm" variant="outline" className="flex-1 h-7 text-[0.7rem]" onClick={() => update(d, "SELESAI", 100)} data-testid={`btn-selesai-${d.id}`}>Selesai</Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
